import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Auth from './auth';
import { auth, db } from '../config/firebase.js';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import SideMenu from './SideMenu';
import NotificationBell from './NotificationBell';
import logger from '../utils/logger';
import { useUser } from '../utils/UserContext';
import useNotifications from '../utils/useNotifications';

function Header({ role, faqOpen, setFaqOpen }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [profileName, setProfileName] = useState(null);
  const navigate = useNavigate();
  const { currentUser: signedInUser } = useUser();
  const canModerate = role === 'moderator' || role === 'admin';
  const notificationState = useNotifications(canModerate ? signedInUser?.uid : null);

  const toggleMenu = () => setMenuOpen(prev => !prev);
  const closeMenu = () => setMenuOpen(false);
  const handleUserChange = (user) => setCurrentUser(user);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      navigate('/');
    } catch (err) {
      logger.error('Logout error:', err);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && menuOpen) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [menuOpen]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!currentUser) {
          setProfileName(null);
          return;
        }
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const data = userDoc.exists() ? userDoc.data() : null;
        const name =
          (data && (data.full_name || data.fullName)) ||
          currentUser.displayName ||
          currentUser.email ||
          null;
        setProfileName(name);
      } catch (e) {
        setProfileName(currentUser?.displayName || currentUser?.email || null);
      }
    };
    fetchProfile();
  }, [currentUser]);

  return (
    <>
      <header className="sticky top-0 w-full bg-gray-900 text-white z-50 shadow-md border-b border-white/5">
        <div className="grid grid-cols-3 items-center px-3 py-3 sm:px-5 max-w-screen-2xl mx-auto">
          {/* Left: hamburger */}
          <div className="flex items-center gap-2">
            <button
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400"
              onClick={toggleMenu}
              aria-expanded={menuOpen}
              aria-controls="side-menu"
              aria-label="Toggle menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Center: logo */}
          <div className="flex justify-center">
            <Link
              to="/"
              onClick={closeMenu}
              className="text-base sm:text-lg font-bold tracking-tight hover:text-brand-300 transition-colors whitespace-nowrap"
            >
              Share2Teach
            </Link>
          </div>

          {/* Right: user / auth */}
          <div className="flex items-center gap-2 justify-end">
            {currentUser ? (
              <>
                {canModerate && <NotificationBell {...notificationState} />}
                <button
                  className="flex items-center gap-2 text-sm text-gray-200 hover:text-white transition-colors rounded-lg px-2 py-1 hover:bg-white/10"
                  title="View account"
                  onClick={() => navigate('/view-account')}
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-white font-semibold text-sm flex-shrink-0">
                    {(profileName || currentUser.displayName || currentUser.email || '?')
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                  <span className="hidden sm:inline truncate max-w-[140px] font-medium">
                    {profileName || currentUser.displayName || currentUser.email}
                  </span>
                </button>
                <button
                  className="bg-brand-600 hover:bg-brand-700 px-3 py-1.5 rounded-lg font-semibold text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  className="hidden sm:inline-block text-sm text-gray-200 hover:text-white font-medium px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400"
                  onClick={() => navigate('/sign-up')}
                >
                  Sign up
                </button>
                <button
                  className="bg-brand-600 hover:bg-brand-700 px-3 py-1.5 rounded-lg font-semibold text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400"
                  onClick={() => navigate('/sign-in')}
                >
                  Login
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ✅ Externalized Side Menu */}
      <SideMenu
        menuOpen={menuOpen}
        closeMenu={closeMenu}
        currentUser={currentUser}
        profileName={profileName}
        role={role}
        setFaqOpen={setFaqOpen}
        moderationUnread={canModerate ? notificationState.unreadCount : 0}
      />

      <Auth onUserChange={handleUserChange} />
    </>
  );
}

export default Header;
